import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { RefreshCw, Download, Ticket, CreditCard, UserPlus, Check } from 'lucide-react';
import toast from 'react-hot-toast';

// ----------------------------------------------------------------
// Tournament Pre-Registrations — the desk's seating + arrivals list
// ----------------------------------------------------------------
// Prepaid tournament buy-ins (paid in-app or on the website via FABI). Staff use
// it to see who prepaid, verify/link web payers to their account, and check them
// in on arrival. Money is booked to the house; this is the seating artifact.

const fmtCents = (c) => (Number(c || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtTime = (d) => (d ? new Date(d).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

const STATUS = {
  success: { label: 'Paid', cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/50' },
  pending: { label: 'Pending', cls: 'bg-gray-700/50 text-gray-300 border-gray-600/50' },
  failed: { label: 'Failed', cls: 'bg-red-900/40 text-red-300 border-red-700/50' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-800 text-gray-500 border-gray-700' },
  refunded: { label: 'Refunded', cls: 'bg-amber-900/30 text-amber-300 border-amber-700/50' },
};

function Badge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

export default function TournamentPrepays() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('success');
  const [busyId, setBusyId] = useState(null);
  const [onlyNotCheckedIn, setOnlyNotCheckedIn] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/tournament-prepays', { params: status ? { status } : {} });
      setRows(res.data.prepays || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load pre-registrations');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // Day-of seating: optionally hide anyone already checked in.
  const visibleRows = onlyNotCheckedIn ? rows.filter((r) => !r.checkedInAt) : rows;

  const playerName = (r) => {
    const u = r.user;
    if (u && (u.firstName || u.lastName)) return `${u.firstName || ''} ${u.lastName || ''}`.trim();
    if (r.firstName || r.lastName) return `${r.firstName || ''} ${r.lastName || ''}`.trim();
    return u?.email || r.contactEmail || 'Unknown player';
  };

  // Contact line under the name — the account email when linked, otherwise
  // whatever the web payer entered so staff have something to verify against.
  const contactLine = (r) => {
    if (r.user?.email) return r.user.email;
    const parts = [];
    if (r.contactEmail) parts.push(r.contactEmail);
    if (r.contactPhone) parts.push(r.contactPhone);
    if (r.contactMemberNumber) parts.push(`#${r.contactMemberNumber}`);
    return parts.join('  ·  ');
  };

  const cancelRow = async (r) => {
    if (!window.confirm(`Cancel the pre-registration for ${playerName(r)}? This clears it from the desk. No charge is affected (it was never paid).`)) return;
    setBusyId(r.id);
    try {
      await api.post(`/admin/tournament-prepays/${r.id}/cancel`);
      toast.success('Pre-registration cancelled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to cancel');
    } finally { setBusyId(null); }
  };

  const refundRow = async (r) => {
    const total = fmtCents(Number(r.amountCents || 0) + Number(r.feeCents || 0));
    if (!window.confirm(`Refund ${playerName(r)}'s ${total} entry? The card/bank is refunded via FABI (the processing fee is not returned).`)) return;
    setBusyId(r.id);
    try {
      await api.post(`/admin/tournament-prepays/${r.id}/refund`);
      toast.success('Entry refunded');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to refund');
    } finally { setBusyId(null); }
  };

  const linkRow = async (r) => {
    const identifier = window.prompt(`Link "${playerName(r)}" to a Players Club account.\nEnter their email, phone, or Players Club member #:`, r.contactEmail || r.contactMemberNumber || '');
    if (identifier == null || !identifier.trim()) return;
    setBusyId(r.id);
    try {
      await api.post(`/admin/tournament-prepays/${r.id}/link`, { identifier: identifier.trim() });
      toast.success('Player linked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not link player');
    } finally { setBusyId(null); }
  };

  const checkInRow = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/tournament-prepays/${r.id}/check-in`);
      toast.success('Checked in');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to check in');
    } finally { setBusyId(null); }
  };

  const undoCheckInRow = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/tournament-prepays/${r.id}/undo-check-in`);
      toast.success('Check-in undone');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to undo check-in');
    } finally { setBusyId(null); }
  };

  const exportCsv = () => {
    if (!visibleRows.length) return;
    const header = ['Player', 'Linked account', 'Contact email', 'Contact phone', 'Member #', 'Tournament', 'Tournament date', 'Buy-in', 'Fee', 'Total', 'Card', 'Status', 'Checked in', 'Paid at', 'FABI txn'];
    const data = visibleRows.map((r) => [
      playerName(r),
      r.user?.email || '',
      r.contactEmail || '',
      r.contactPhone || '',
      r.contactMemberNumber || '',
      r.tournamentName || r.tournamentId || '',
      r.tournamentDate ? new Date(r.tournamentDate).toLocaleString() : '',
      (Number(r.amountCents || 0) / 100).toFixed(2),
      (Number(r.feeCents || 0) / 100).toFixed(2),
      ((Number(r.amountCents || 0) + Number(r.feeCents || 0)) / 100).toFixed(2),
      r.cardBrand ? `${r.cardBrand} ****${r.cardLastFour || ''}` : '',
      STATUS[r.status]?.label || r.status,
      r.checkedInAt ? new Date(r.checkedInAt).toLocaleString() : '',
      r.paidAt ? new Date(r.paidAt).toLocaleString() : '',
      r.fabiTransactionId || '',
    ]);
    const csv = [header, ...data].map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tournament-prepays-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="max-w-[1200px] mx-auto p-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <Ticket className="text-amber-400" /> Tournament Pre-Registrations
          </h1>
          <p className="text-sm text-gray-400">
            Players who prepaid a tournament buy-in in the app or on the website. Verify, check them in on arrival — the buy-in is already paid.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
            >
              <option value="success">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
              <option value="">All</option>
            </select>
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-300 select-none cursor-pointer">
            <input type="checkbox" checked={onlyNotCheckedIn} onChange={(e) => setOnlyNotCheckedIn(e.target.checked)} className="accent-emerald-500 h-4 w-4" />
            Only not checked in
          </label>
          <button onClick={load} disabled={loading} className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-50">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={exportCsv} disabled={!visibleRows.length} className="flex items-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 px-3 py-2 text-sm font-medium text-black disabled:opacity-40">
            <Download size={15} /> CSV
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {onlyNotCheckedIn && rows.length ? 'Everyone here is checked in. 🎉' : `No pre-registrations${status ? ` (${STATUS[status]?.label || status})` : ''} yet.`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="py-3 px-4 font-medium">Player</th>
                  <th className="py-3 px-4 font-medium">Tournament</th>
                  <th className="py-3 px-4 font-medium text-right">Total</th>
                  <th className="py-3 px-4 font-medium">Method</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  <th className="py-3 px-4 font-medium">Checked in</th>
                  <th className="py-3 px-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => {
                  const busy = busyId === r.id;
                  const contact = contactLine(r);
                  return (
                    <tr key={r.id} className="border-b border-gray-800/60 hover:bg-gray-700/20 align-top">
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-100 font-medium">{playerName(r)}</span>
                          {r.user ? (
                            <span className="rounded border border-sky-800/60 bg-sky-900/30 text-sky-300 px-1.5 py-0.5 text-[10px] font-medium">Linked</span>
                          ) : (
                            <span className="rounded border border-gray-600 bg-gray-700/40 text-gray-400 px-1.5 py-0.5 text-[10px] font-medium">Not linked</span>
                          )}
                        </div>
                        {contact ? <div className="text-xs text-gray-500 mt-0.5">{contact}</div> : null}
                      </td>
                      <td className="py-2.5 px-4">
                        <div className="text-gray-200">{r.tournamentName || r.tournamentId || '—'}</div>
                        {r.tournamentDate ? <div className="text-xs text-gray-500">{new Date(r.tournamentDate).toLocaleString()}</div> : null}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono text-amber-300">{fmtCents(Number(r.amountCents || 0) + Number(r.feeCents || 0))}</td>
                      <td className="py-2.5 px-4 text-gray-300">
                        {r.cardBrand ? (
                          <span className="inline-flex items-center gap-1.5"><CreditCard size={13} className="text-gray-500" /> {r.cardBrand} ****{r.cardLastFour || ''}</span>
                        ) : r.status === 'success' ? (
                          <span className="text-gray-400">Bank</span>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="py-2.5 px-4"><Badge status={r.status} /></td>
                      <td className="py-2.5 px-4">
                        {r.status === 'success' ? (
                          r.checkedInAt ? (
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-medium"><Check size={13} /> {fmtTime(r.checkedInAt)}</span>
                              <button disabled={busy} onClick={() => undoCheckInRow(r)} className="text-[11px] text-gray-500 hover:text-gray-300 underline disabled:opacity-50">undo</button>
                            </div>
                          ) : (
                            <button disabled={busy} onClick={() => checkInRow(r)} className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-white px-2.5 py-1 text-xs font-semibold disabled:opacity-50">
                              Check in
                            </button>
                          )
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          {!r.user && (r.status === 'success' || r.status === 'pending') ? (
                            <button disabled={busy} onClick={() => linkRow(r)} className="inline-flex items-center gap-1 rounded-md border border-sky-800/60 text-sky-300 hover:bg-sky-900/30 px-2.5 py-1 text-xs font-medium disabled:opacity-50">
                              <UserPlus size={13} /> Link
                            </button>
                          ) : null}
                          {r.status === 'success' ? (
                            <button disabled={busy} onClick={() => refundRow(r)} className="rounded-md border border-amber-800/60 text-amber-300 hover:bg-amber-900/30 px-2.5 py-1 text-xs font-medium disabled:opacity-50">
                              Refund
                            </button>
                          ) : r.status !== 'cancelled' && r.status !== 'refunded' ? (
                            <button disabled={busy} onClick={() => cancelRow(r)} className="rounded-md border border-red-800/60 text-red-300 hover:bg-red-900/30 px-2.5 py-1 text-xs font-medium disabled:opacity-50">
                              Cancel
                            </button>
                          ) : null}
                          {r.user || r.status === 'success' || r.status === 'pending' ? null : <span className="text-gray-700">—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
